import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { memo, useCallback, useRef, useState } from "react";

export const ScoutInput = memo(function ScoutThreadInput({
  onSubmit,
  isSending,
  placeholder = "Chat with Scout…",
}: {
  onSubmit: (message: string) => Promise<void>;
  isSending: boolean;
  placeholder?: string;
}) {
  const [hasContent, setHasContent] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isSubmitDisabled = !hasContent || isSending;

  const clearInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setHasContent(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    const value = inputRef.current?.value ?? "";
    if (!value.trim() || isSending) return;
    await onSubmit(value.trim());
    clearInput();
  }, [isSending, onSubmit, clearInput]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHasContent = !!e.target.value.trim();
    setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      } else if (event.key === "Escape") {
        event.preventDefault();
        const textarea = inputRef.current;
        if (textarea && textarea.value.trim()) {
          clearInput();
          textarea.focus();
        } else if (textarea) {
          textarea.blur();
        }
      }
    },
    [handleSubmit, clearInput]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 1,
        width: "100%",
        maxWidth: 920,
        mx: "auto",
        px: 2,
        py: 1.25,
        borderRadius: (theme) => theme.spacing(3),
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <TextField
        multiline
        minRows={1}
        maxRows={8}
        fullWidth
        variant="standard"
        InputProps={{ disableUnderline: true }}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={isSending}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        inputProps={{
          "aria-label": "Message Scout",
          style: {
            padding: "4px 0",
            minHeight: 24,
            lineHeight: 1.5,
          },
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
          },
        }}
      />
      <Tooltip title="Send (Enter · Shift+Enter for new line)" arrow>
        <span
          style={{
            display: "inline-flex",
            flexShrink: 0,
            cursor: isSubmitDisabled ? "default" : "pointer",
          }}
        >
          <IconButton
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            aria-label="Send message"
            color="primary"
          >
            <ArrowUpwardIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
});
ScoutInput.displayName = "ScoutThreadInput";
